import { isTieredVariant } from '@lub0v/santa-utils';
import { purchasedTier, premiumTierName } from '@src/components/PremiumBadge/tierIdentity';
import { PremiumAccessSummary } from '@src/components/PremiumBadge/PremiumAccessSummary';
import { PremiumTierPicker, PremiumTierDescription } from './PremiumTierPicker';
import { PremiumPurchaseSurvey, type PurchaseSurveyHandle } from './PremiumPurchaseSurvey';
import { PremiumOfferView } from '@src/components/PremiumUpsell/PremiumOfferView';
import { UserAccountEmailInput } from '@lub0v/santa-utils';
import { translateFieldError, validateFieldError } from '@src/validation';
import { Link } from '@src/components/Link/Link';
import { usePremiumModal, type PremiumFeature } from '@src/components/PremiumUpsell/PremiumModal';
import { PremiumRecoveryPrompt } from './PremiumRecoveryPrompt';
import { readPremiumReturn, safePremiumReturn, savePremiumReturn } from '@src/utils/premiumReturn';
import { CopyIcon } from '@src/components/icons/CopyIcon';
import { trackPremium, premiumBuyAttribution, trackPremiumPurchaseResult, capturePremiumCheckoutAttribution } from '@src/utils/premiumAnalytics';
import { useEffect, useRef, useState } from 'react';
import { showDialog } from '@src/components/Dialog/Dialog';
import { supportEmail, isDotCom } from '@src/site';
import { showToast } from '@src/components/Toast';
import { CardDecoration } from '@src/theme/Decoration';
import { useTheme } from '@src/theme/useTheme';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Shell } from '@src/components/Shell/Shell';
import { PageContent } from '@src/components/PageContent/PageContent';
import { CrownIcon } from '@src/components/icons/CrownIcon';
import { Button, Hint, EmailInput, Panel, SettingsSteps, Spinner, Stack, Typography } from '@src/components';
import { PremiumBadge } from '@src/components/PremiumBadge/PremiumBadge';
import { GuestEmailLink } from '@src/pages/account/GuestEmailLink';
import { loadSession } from '@src/state/sessionSlice';
import { useAppDispatch } from '@src/state/hooks';
import { useAppSelector } from '@src/state/hooks';
import { usePremiumOffer } from './PremiumOfferProvider';
import './PremiumBuyPage.css';

export function PremiumBuyPage() {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user, status } = useAppSelector((state) => state.session);
  const offer = usePremiumOffer();
  const directPayment = !isDotCom();
  const [searchParams] = useSearchParams();
  const paymentResult = searchParams.get('result');
  const location = useLocation();
  const surveyRef = useRef<PurchaseSurveyHandle>(null);
  const trackedReturn = useRef<string | null>(null);
  const paymentReturn = ['success', 'failed'].includes(paymentResult || '');
  const automaticPayment = searchParams.get('automaticPayment') !== 'false' || paymentReturn;
  const paymentParams = new URLSearchParams();
  if (automaticPayment && searchParams.has('price')) {
    paymentParams.set('automaticPayment', 'true');
    paymentParams.set('price', searchParams.get('price') || '');
  }
  if (isTieredVariant(offer.variant)) paymentParams.set('tier', searchParams.get('tier') || 'tier_2');
  const priceQuery = paymentParams.size ? `?${paymentParams}` : '';
  const awaitingConfirmation = paymentReturn && paymentResult === 'success';
  const paymentFailed = paymentReturn && paymentResult === 'failed';
  const dispatch = useAppDispatch();
  const userId = user?.id;
  const activeTier = purchasedTier(user);
  const upgradeTarget = isTieredVariant(user?.premium?.variant)
    ? user?.premium?.offers.find(item => item.tier === searchParams.get('tier') && item.status === 'upgrade') : undefined;
  const isUpgrade = Boolean(user?.isPremium && upgradeTarget);
  const isPremium = Boolean(user?.isPremium && !isUpgrade);
  const activeName = activeTier ? premiumTierName(activeTier, i18n.language.startsWith('ru')) : t('account.plan.premium');
  const [surveyRecoveryUser, setSurveyRecoveryUser] = useState<number | null>(null);
  const returnTo = safePremiumReturn(searchParams.get('returnTo')) || (userId ? readPremiumReturn(userId) : null);
  const openPremium = usePremiumModal();
  const requestedFeature = searchParams.get('feature');
  const comparisonFeature: PremiumFeature = ['account', 'address', 'questions', 'history', 'themes', 'organizers', 'draw'].includes(requestedFeature || '')
    ? requestedFeature as PremiumFeature : 'account';
  useEffect(() => {
    if (returnTo && userId) savePremiumReturn(returnTo, userId);
  }, [returnTo, userId]);
  const automaticCheckout = automaticPayment && Boolean(user && (!user.isPremium || isUpgrade)) && !awaitingConfirmation;
  const attemptKey = `${user?.id ?? ''}:${offer.offerCode ?? ''}:${offer.amountMinor ?? ''}:${offer.checkoutAmountMinor ?? ''}`;
  const [attempt, setAttempt] = useState<{
    key: string;
    id: number;
    token?: string;
    existing: boolean;
    referenceCode: string;
    amount: string;
    status: string;
    paymentUrl: string | null;
    receiptEmail?: string | null;
  } | null>(null);
  const [receiptEmailBlurred, setReceiptEmailBlurred] = useState(false);
  const [receiptInput, setReceiptInput] = useState<{ userId: number; value: string } | null>(null);
  const [paymentError, setPaymentError] = useState(false);
  const [openingPayment, setOpeningPayment] = useState(false);
  const [copiedPayment, setCopiedPayment] = useState<string | null>(null);
  const currentAttempt = attempt?.key === attemptKey ? attempt : null;
  const receiptEmail = currentAttempt?.receiptEmail || (receiptInput && receiptInput.userId === userId ? receiptInput.value : user?.email || '');
  const receiptEmailError = validateFieldError(new UserAccountEmailInput(), 'email', { email: receiptEmail.trim() });
  const shouldCheckPayment = automaticPayment && Boolean(currentAttempt?.existing || paymentReturn) && Boolean(userId) && !isPremium;
  const displayPrice = offer.checkoutAmountMinor !== undefined ? offer.symbolPrice :
    (directPayment || automaticPayment) && currentAttempt
      ? isDotCom()
        ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(currentAttempt.amount))
        : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(Number(currentAttempt.amount))} ₽`
      : offer.symbolPrice;
  useEffect(() => {
    if (!userId || isPremium || !offer.ready || !(automaticPayment ? offer.paymentsAvailable : offer.paymentUrl)) return;
    let active = true;
    const controller = new AbortController();
    void fetch(`/api/premium/payment${priceQuery}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error('Payment unavailable');
        const data = await response.json();
        if (active) {
          setPaymentError(false);
          setAttempt({
            key: attemptKey,
            id: data.id,
            token: data.token,
            existing: Boolean(data.existing),
            referenceCode: data.referenceCode,
            amount: data.amount,
            status: data.status,
            paymentUrl: data.paymentUrl,
            receiptEmail: data.receiptEmail,
          });
        }
      })
      .catch(() => {
        if (active) setPaymentError(true);
      });
    return () => {
      active = false;
      controller.abort();
    };
  }, [attemptKey, userId, isPremium, offer.ready, offer.paymentUrl, offer.paymentsAvailable, automaticPayment, priceQuery]);
  useEffect(() => {
    if (!shouldCheckPayment) return;
    const controller = new AbortController();
    let busy = false;
    async function check() {
      if (busy || document.hidden) return;
      busy = true;
      try {
        const response = await fetch('/api/premium/payment/state', { signal: controller.signal, cache: 'no-store', credentials: 'same-origin' });
        if (response.ok && (await response.json()).alreadyPaid && !controller.signal.aborted) await dispatch(loadSession({ background: true }));
      } catch { /* Retry transient connectivity errors without interrupting checkout. */ }
      finally { busy = false; }
    }
    void check();
    const timer = window.setInterval(() => void check(), 5000);
    window.addEventListener('focus', check);
    return () => { controller.abort(); window.clearInterval(timer); window.removeEventListener('focus', check); };
  }, [shouldCheckPayment, userId, dispatch]);
  useEffect(() => {
    const key = `${location.key}:${paymentResult}`;
    if (!paymentReturn || !offer.ready || trackedReturn.current === key) return;
    trackedReturn.current = key;
    trackPremiumPurchaseResult(paymentResult, offer);
  }, [location.key, paymentReturn, paymentResult, offer]);
  async function openDirectPayment() {
    if (!currentAttempt || openingPayment || currentAttempt.status === 'paid') return;
    if (automaticPayment && receiptEmailError) return;
    // Snapshot attribution before window.open clones this tab's session storage.
    if (automaticPayment) capturePremiumCheckoutAttribution();
    // Reserve the tab during the click so browsers do not block it after the request.
    const tab = window.open('about:blank', '_blank');
    if (!tab) {
      showToast(t('premiumBuy.allowPopup'));
      return;
    }
    tab.opener = null;
    setOpeningPayment(true);
    try {
      const response = await fetch(automaticPayment ? '/api/premium/payment/checkout' : '/api/premium/payment/opened', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: currentAttempt.id, token: currentAttempt.token, ...(automaticPayment ? { receiptEmail: receiptEmail.trim() } : {}) }),
      });
      if (!response.ok) throw new Error('Payment unavailable');
      const { paymentUrl, id, receiptEmail: checkoutEmail } = await response.json();
      const url = new URL(paymentUrl);
      const localCheckout = import.meta.env.DEV && ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)
        && url.origin === window.location.origin && url.pathname === '/premium/local-checkout';
      if (url.protocol !== 'https:' && !localCheckout) throw new Error('Invalid payment URL');
      tab.location.href = url.href;
      setAttempt({ ...currentAttempt, id, status: 'pending', existing: true, receiptEmail: checkoutEmail });
      trackPremium('premium_buy_click_v2', {
        ...premiumBuyAttribution(window.location.search),
        placement: 'payment_button',
        action: 'open_payment_link',
        ...(isTieredVariant(offer.variant) ? {
          purchaseType: isUpgrade ? 'upgrade' as const : 'initial_purchase' as const,
          currentTier: activeTier ?? 'free',
          targetTier: selectedTier,
        } : {}),
      });
    } catch {
      tab.close();
      showToast(t('premiumBuy.directError'));
    } finally {
      setOpeningPayment(false);
    }
  }
  async function copyPaymentCode() {
    if (!currentAttempt) return;
    try {
      await navigator.clipboard.writeText(currentAttempt.referenceCode);
      setCopiedPayment(`${currentAttempt.key}:${currentAttempt.referenceCode}`);
      showToast(t('admin.payments.copied'), 'success');
    } catch {
      showToast(t('box.organizer.copyFailed'));
    }
  }
  async function openPayment() {
    if (!currentAttempt || openingPayment) return;
    setOpeningPayment(true);
    let previous: {
      alreadyPaid: boolean;
      payment: { id: number; status: string; reference_code: string } | null;
    };
    try {
      const response = await fetch('/api/premium/payment/state', {
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error('Payment state unavailable');
      previous = await response.json();
    } catch {
      showToast(t('premiumBuy.manualError'));
      return;
    } finally {
      setOpeningPayment(false);
    }
    const contact = <a href={`mailto:${supportEmail()}`}>{t('premiumBuy.paymentContact')}</a>;
    if (previous.alreadyPaid) {
      showDialog({
        title: t('premiumBuy.alreadyPaidTitle'),
        children: (
          <Stack gap={4}>
            <Typography as="p" role="body/base">
              {t('premiumBuy.alreadyPaidBody')}
            </Typography>
            {contact}
          </Stack>
        ),
        cancel: t('premiumBuy.manualCancel'),
        testId: 'premium-payment-already-paid',
      });
      return;
    }
    const hasPrevious = Boolean(
      previous.payment &&
      (currentAttempt.existing ||
        previous.payment.id !== currentAttempt.id ||
        previous.payment.status === 'pending'),
    );
    trackPremium('premium_buy_click_v2', {
      ...premiumBuyAttribution(window.location.search),
      placement: 'payment_button',
      action: 'request_payment_link',
    });
    showDialog({
      title: t(hasPrevious ? 'premiumBuy.existingPaymentTitle' : 'premiumBuy.manualTitle'),
      children: (
        <Stack gap={4}>
          {hasPrevious && (
            <>
              <Typography as="p" role="body/base">
                {t('premiumBuy.existingPaymentBody')}
              </Typography>
              {contact}
            </>
          )}
          {!hasPrevious && (
            <>
              <Typography as="p" role="body/base">
                {t('premiumBuy.manualBody')}
              </Typography>
              <Typography as="p" role="body/base">
                {t('premiumBuy.manualDelivery', { email: user?.email })}
              </Typography>
            </>
          )}
        </Stack>
      ),
      submit: t(hasPrevious ? 'premiumBuy.retryPayment' : 'premiumBuy.manualRequest'),
      cancel: t('premiumBuy.manualCancel'),
      testId: 'premium-payment-request-dialog',
      onCancel: () =>
        trackPremium('premium_email_request_cancelled_v2', {
          ...premiumBuyAttribution(window.location.search),
          placement: 'payment_dialog',
          action: 'cancel',
        }),
      onSubmit: async () => {
        setOpeningPayment(true);
        try {
          const response = await fetch('/api/premium/payment/pending', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: currentAttempt.id, token: currentAttempt.token }),
          });
          if (!response.ok) throw new Error('Payment request unavailable');
          const result = await response.json();
          setAttempt({ ...currentAttempt, existing: true });
          if (result.sent)
            trackPremium('premium_email_request_sent_v2', {
              ...premiumBuyAttribution(window.location.search),
              placement: 'payment_dialog',
              action: 'email_sent',
            });
          showToast(t('premiumBuy.manualSuccess', { email: user?.email }), 'success');
        } catch (error) {
          showToast(t('premiumBuy.manualError'));
          throw error;
        } finally {
          setOpeningPayment(false);
        }
      },
    });
  }

  const tierNames = i18n.language.startsWith('ru')
    ? { tier_1: 'Волшебником', tier_2: 'Санта-Мастером', tier_3: 'Санта-Магистром' }
    : { tier_1: 'Wizard', tier_2: 'Master', tier_3: 'Grandmaster' };
  const requestedTier = searchParams.get('tier');
  const selectedTier = !isTieredVariant(offer.variant) ? 'tier_3' : ['tier_1', 'tier_2', 'tier_3'].includes(requestedTier || '') ? requestedTier! : 'tier_2';
  const tierName = tierNames[selectedTier as keyof typeof tierNames] || tierNames.tier_2;
  const tierPurchaseTitle = i18n.language.startsWith('ru')
    ? (isUpgrade ? `Стать ${tierName}, доплатив ${displayPrice}` : `Стать ${tierName} за ${displayPrice}`)
    : (isUpgrade ? `Upgrade to ${tierName} for ${displayPrice}` : `Become a ${tierName} for ${displayPrice}`);

  const steps = (path: string, count: number) =>
    Array.from({ length: count }, (_, index) => t(`premiumBuy.${path}.${index}`));
  return (
    <Shell>
      <PageContent className="premium-buy" testId="premium-buy">
        <header className={`premium-buy__offer${automaticCheckout ? ' premium-buy__offer--checkout' : ''}`} data-premium-tier={isPremium ? activeTier : offer.variant ? selectedTier : undefined} data-testid="premium-buy-offer">
          <CardDecoration variant={3} />
          <span className={`premium-buy__crown${(isPremium ? activeTier : offer.ready ? selectedTier : undefined) ? ` premium-buy__crown--${isPremium ? activeTier : selectedTier}` : ''}`} aria-hidden="true">
            <CrownIcon />
          </span>
          {!automaticCheckout && (
            <Typography as="p" role="title/eyebrow">
              {isPremium ? activeName : t('premiumBuy.title')}
            </Typography>
          )}
          <Typography as="h1" role="title/page">
            {offer.variant && !isPremium && !paymentReturn ? tierPurchaseTitle : (isPremium && activeTier ? <span className="premium-buy__active-title"><span>{i18n.language.startsWith('ru') ? 'Ваш тариф' : 'Your plan'}</span><span className="premium-buy__active-title-line"><span className="premium-buy__active-tier-name">{i18n.language.startsWith('ru') ? `«${activeName}»` : activeName}</span>{' '}<span className="premium-buy__active-title-status">{i18n.language.startsWith('ru') ? 'активен' : 'is active'}</span></span></span> : t(isPremium ? 'premiumBuy.activeTitle' : awaitingConfirmation ? 'premiumBuy.confirmingTitle' : paymentFailed ? 'premiumBuy.failedTitle' : automaticCheckout ? 'premiumBuy.buyPremiumPrice' : 'premiumBuy.heroTitle', { price: displayPrice }))}
          </Typography>
          {isTieredVariant(offer.variant) && !isPremium && !paymentReturn && <PremiumTierDescription tier={selectedTier} />}
          <Typography as="p" role="body/base">
            {isPremium ? t('premiumBuy.activeThanks') : <PremiumOfferView placement="buy_offer" {...premiumBuyAttribution(window.location.search)} enabled={!paymentReturn && (!currentAttempt || Number(currentAttempt.amount) === (offer.checkoutAmountMinor !== undefined ? offer.checkoutAmountMinor / 100 : offer.amount))}>{t('premiumBuy.offer', { price: displayPrice })}</PremiumOfferView>}
          </Typography>
          {offer.checkoutAmountMinor !== undefined && !isPremium && <Typography as="p" role="body/base" testId="premium-test-price">{i18n.language.startsWith('ru') ? 'Тестовый платёж: ' : 'Test payment: '}{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: offer.currency }).format(offer.checkoutAmountMinor / 100)}</Typography>}
          {isTieredVariant(offer.variant) && !isPremium && !awaitingConfirmation && <PremiumTierPicker disabled={openingPayment} />}
          {!isPremium && <Link testId="premium-buy-learn-more"
            onClick={() => openPremium(comparisonFeature, premiumBuyAttribution(window.location.search).context, 'checkout_learn_more', 'learn_more')}>
            {t('premiumBuy.learnMore')}
          </Link>}
        </header>
        {status === 'idle' || status === 'loading' ? (
          <Spinner label={t('account.loading')} />
        ) : isPremium && user ? (
          <Panel edges={{ pieces: 3 }} padding="large" className="premium-buy__celebration" testId="premium-buy-active">
            <div className="premium-buy__celebration-heading">
              <PremiumBadge tier={activeTier}>{activeName}</PremiumBadge>
              <Typography as="h2" role="title/card">{t('premiumBuy.activeWelcome')}</Typography>
              <PremiumAccessSummary user={user} />
              <Typography as="p" role="body/base">{t('premiumPage.accessUntilApril302027')}</Typography>
            </div>
            {user.isGuest && (
              <Stack gap={4} wide testId="premium-buy-keep-access">
                <Typography as="h2" role="title/card">{t('premiumBuy.keepAccessTitle')}</Typography>
                <Typography as="p" role="body/base">{t('premiumBuy.keepAccessBody')}</Typography>
                <PremiumRecoveryPrompt userId={user.id} onReady={setSurveyRecoveryUser} />
                <GuestEmailLink />
              </Stack>
            )}
            <PremiumPurchaseSurvey ref={surveyRef} userId={userId} enabled={Boolean(!user.isGuest || surveyRecoveryUser === userId)} />
            <Button variant="primary" size="large" fullWidth testId="premium-continue" onClick={async () => {
              await surveyRef.current?.flush();
              const target = new URL(returnTo || '/account/boxes', window.location.origin);
              if (target.origin === window.location.origin) navigate(target.pathname + target.search + target.hash);
              else window.location.assign(target.href);
            }}>{t('premiumBuy.continue')} →</Button>
          </Panel>
        ) : awaitingConfirmation && user ? (
          <Panel edges={{ pieces: 3 }} padding="large" testId="premium-payment-confirming">
            <Stack gap={4}>
              <Typography as="h2" role="title/card">{t('premiumBuy.confirmingTitle')}</Typography>
              <Hint tone="info" testId="premium-payment-status-hint">{t('premiumBuy.confirmingBody')}</Hint>
              <Typography as="p" role="body/base">{t('premiumBuy.confirmingWait')}</Typography>
              <a className="premium-buy__payment-contact" href={`mailto:${supportEmail()}`}>{t('premiumBuy.paymentHelpContact')}</a>
            </Stack>
          </Panel>
        ) : offer.site === 'com' && !offer.paymentsAvailable ? (
          <Panel edges={{ pieces: 3 }} padding="large" testId="premium-buy-coming-soon">
            <Stack gap={3}>
              <Typography as="h2" role="title/card">
                {t('premiumBuy.comSoonTitle')}
              </Typography>
              <Typography as="p" role="body/base">
                {t('premiumBuy.comSoonBody')}
              </Typography>
            </Stack>
          </Panel>
        ) : !user ? (
          <Panel edges={{ pieces: 3 }} padding="large">
            <Stack gap={4}>
              <Typography as="h2" role="title/card">
                {t('premiumBuy.signInTitle')}
              </Typography>
              <Typography as="p" role="body/base">
                {t('premiumBuy.signInBody')}
              </Typography>
              <Button onClick={() => navigate('/login')}>{t('premiumBuy.login')}</Button>
              <Button variant="secondary" onClick={() => navigate('/register')}>
                {t('premiumBuy.register')}
              </Button>
            </Stack>
          </Panel>
        ) : user.isGuest && !automaticPayment ? (
          <Stack gap={5} wide>
            <Panel edges={{ pieces: 3 }} padding="large">
              <Stack gap={4}>
                <Typography as="h2" role="title/card">
                  {t('premiumBuy.guestTitle')}
                </Typography>
                <Typography as="p" role="body/base">
                  {t('premiumBuy.guestBody')}
                </Typography>
                <Typography as="p" role="body/strong">
                  {t('premiumBuy.preserved')}
                </Typography>
              </Stack>
            </Panel>
            <Panel edges={{ pieces: 3 }} padding="large" testId="premium-buy-email">
              <Stack gap={4}>
                <Typography as="h2" role="title/card">
                  {t('premiumBuy.emailTitle')}
                </Typography>
                <SettingsSteps steps={steps('emailSteps', 3)} />
                <GuestEmailLink />
              </Stack>
            </Panel>
            <Panel edges={{ pieces: 3 }} padding="large" testId="premium-buy-existing">
              <Stack gap={4}>
                <Typography as="h2" role="title/card">
                  {t('premiumBuy.existingTitle')}
                </Typography>
                <SettingsSteps steps={steps('existingSteps', 4)} />
                <Button variant="secondary" onClick={() => navigate('/login')}>
                  {t('premiumBuy.login')}
                </Button>
              </Stack>
            </Panel>
          </Stack>
        ) : automaticCheckout ? (
          <Panel edges={{ pieces: 3 }} padding="large" testId="premium-buy-account">
            <Stack gap={4}>
              <Typography as="h2" role="title/card">{t('premiumBuy.receiptTitle')}</Typography>
              {paymentFailed && (
                <Hint tone="error" testId="premium-payment-failed">
                  {t('premiumBuy.failedBody')}{' '}<a href={`mailto:${supportEmail()}`}>{supportEmail()}</a>
                </Hint>
              )}
              <Typography as="p" role="body/base">{t('premiumBuy.readyBody')}</Typography>
              <form className="premium-buy__receipt-form" onSubmit={(event) => { event.preventDefault(); void openDirectPayment(); }}>
                <EmailInput
                  onFocus={() => setReceiptEmailBlurred(false)}
                  onBlur={() => setReceiptEmailBlurred(true)}
                  error={receiptEmailBlurred && receiptEmail.trim() && receiptEmailError ? translateFieldError(receiptEmailError, t) : undefined}
                  name="receiptEmail"
                  autoComplete="email"
                  label={t('premiumBuy.receiptEmail')}
                  hint={t(currentAttempt?.receiptEmail ? 'premiumBuy.receiptEmailFixed' : user.email ? 'premiumBuy.receiptEmailHint' : 'premiumBuy.receiptEmailGuestHint')}
                  value={receiptEmail}
                  onValueChange={(value) => { if (userId) setReceiptInput({ userId, value }); }}
                  required
                  maxLength={64}
                  readOnly={Boolean(currentAttempt?.receiptEmail)}
                  disabled={openingPayment}
                  testId="premium-receipt-email"
                />
                {paymentError || offer.failed ? (
                  <Stack gap={3} wide>
                    <Hint>{t('premiumBuy.paymentError')}</Hint>
                    <Button variant="secondary" onClick={() => window.location.reload()}>{t('premiumBuy.offerRetry')}</Button>
                  </Stack>
                ) : (
                  <Button type="submit" variant={theme === 'dark' ? 'premium-light' : 'premium-dark'} className="premium-buy__checkout-button" size="large" fullWidth icon={<CrownIcon />}
                    disabled={!currentAttempt || Boolean(receiptEmailError) || openingPayment || currentAttempt.status === 'paid'}
                    loading={openingPayment || !offer.ready} testId="premium-buy-payment">
                    {i18n.language.startsWith('ru') ? `Стать ${tierName} за ${displayPrice}` : `Become a ${tierName} for ${displayPrice}`}
                  </Button>
                )}
              </form>
            </Stack>
          </Panel>
        ) : (
          <Panel edges={{ pieces: 3 }} padding="large" testId="premium-buy-account">
            <Stack gap={4}>
              <Typography as="h2" role="title/card">
                {t('premiumBuy.readyTitle')}
              </Typography>
              {directPayment && !automaticPayment &&
                currentAttempt &&
                (currentAttempt.existing ||
                  ['pending', 'paid'].includes(currentAttempt.status)) && (
                  <Hint
                    tone={currentAttempt.status === 'paid' ? 'success' : undefined}
                    testId="premium-payment-wait"
                  >
                    <Stack gap={2}>
                      <span>
                        {t(
                          currentAttempt.status === 'paid'
                            ? 'premiumBuy.directPaid'
                            : 'premiumBuy.directWait',
                        )}
                      </span>
                      <a href={`mailto:${supportEmail()}`}>{t('premiumBuy.paymentContact')}</a>
                    </Stack>
                  </Hint>
                )}
              <Typography as="p" role="body/base">
                {t('premiumBuy.readyBody')}
              </Typography>
              {!offer.ready ? (
                <>
                  <Typography as="p" role="body/base">
                    {t(offer.failed ? 'premiumBuy.offerFailed' : 'premiumBuy.offerLoading')}
                  </Typography>
                  {offer.failed && (
                    <Button variant="secondary" onClick={() => window.location.reload()}>
                      {t('premiumBuy.offerRetry')}
                    </Button>
                  )}
                </>
              ) : offer.paymentUrl ? (
                <Stack gap={4} wide>
                  {paymentError && (
                    <>
                      <Hint>{t('premiumBuy.paymentError')}</Hint>
                      <Button variant="secondary" onClick={() => window.location.reload()}>
                        {t('premiumBuy.offerRetry')}
                      </Button>
                    </>
                  )}
                  {directPayment && !automaticPayment && currentAttempt && currentAttempt.status !== 'paid' && (
                    <Stack gap={3} wide>
                      <>
                        <Typography as="p" role="body/base">
                          {t('premiumBuy.directIntro')}
                        </Typography>
                        <Typography as="p" role="body/strong">
                          {t('premiumBuy.directCode')}
                        </Typography>
                        <Button
                          variant={
                            copiedPayment ===
                            `${currentAttempt.key}:${currentAttempt.referenceCode}`
                              ? 'secondary'
                              : 'primary'
                          }
                          icon={<CopyIcon />}
                          onClick={() => void copyPaymentCode()}
                          testId="premium-payment-code"
                        >
                          {t(
                            copiedPayment ===
                              `${currentAttempt.key}:${currentAttempt.referenceCode}`
                              ? 'premiumBuy.codeCopied'
                              : 'premiumBuy.copyCode',
                            { code: currentAttempt.referenceCode },
                          )}
                        </Button>
                        <Typography as="p" role="body/base">
                          {t('premiumBuy.directReceipt', { email: user.email })}
                        </Typography>
                      </>
                    </Stack>
                  )}
                  {directPayment && !automaticPayment && currentAttempt?.status !== 'paid' && (
                    <Hint testId="premium-payment-certificate-hint">
                      <Stack gap={2}>
                        <strong>{t('premiumBuy.certificateTitle')}</strong>
                        <span>{t('premiumBuy.certificateBody')}</span>
                        <a href="https://browser.yandex.ru/" target="_blank" rel="noopener noreferrer">
                          {t('premiumBuy.certificateBrowser')}
                        </a>
                        <a href="https://status.tbank-online.com/certificates/" target="_blank" rel="noopener noreferrer">
                          {t('premiumBuy.certificateGuide')}
                        </a>
                        <span>{t('premiumBuy.paymentHelpBody')}</span>
                        <a href={`mailto:${supportEmail()}`}>{t('premiumBuy.paymentHelpContact')}</a>
                      </Stack>
                    </Hint>
                  )}
                  <Button
                    variant="premium-dark"
                    className="premium-buy__checkout-button"
                    icon={<CrownIcon />}
                    disabled={
                      !currentAttempt ||
                      openingPayment ||
                      (directPayment &&
                        (!currentAttempt.paymentUrl || currentAttempt.status === 'paid'))
                    }
                    loading={openingPayment}
                    onClick={() => void (directPayment ? openDirectPayment() : openPayment())}
                    testId="premium-buy-payment"
                  >
                    {t('premiumBuy.pay', { price: displayPrice })}
                  </Button>
                </Stack>
              ) : (
                <Typography as="p" role="body/base">
                  {t('premiumBuy.paymentPending')}
                </Typography>
              )}
            </Stack>
          </Panel>
        )}
      </PageContent>
    </Shell>
  );
}
